import { Sync } from "@mui/icons-material";
import { Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableFooter,
	TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { FinancialService } from "../../../services/FinancialService";
import { FinancialBillDTO, FinancialOverviewDTO } from "../../../types/FinancialDTO";
import { compareOverviewRows, formatCurrency, matchesStatusFilter, monthLabel, statusColor } from "../../../utils/financialFunctions";
import { useAlert } from "../../contextProviders/AlertProvider";
import "./FinancialOverview.scss";

const PERIODS = [3, 6, 12];

const STATUS_FILTERS = [['ALL', 'Todas'], ['OVERDUE', 'Algum faturamento atrasado'], ['CURRENT_OVERDUE', 'Faturamento atual atrasado'],
	['OPEN', 'Faturamento aberto']];

const TOTALS = [['Total faturado', undefined, '#64b5f6'], ['Total pago', 'PAID', '#81c784'], ['Total em aberto', 'OPEN', '#ffb74d']];

const sum = (bills: FinancialBillDTO[] = []) => bills.reduce((total, bill) => total + (bill.value || 0), 0);

const cellStatus = (bills: FinancialBillDTO[]) => {
	if (bills.some(bill => bill.status === 'OVERDUE')) {
		return 'OVERDUE';
	}
	return bills.some(bill => bill.status === 'OPEN') ? 'OPEN' : 'PAID';
};

const FinancialOverview: FC = () => {
	const [months, setMonths] = useState(3);
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('ALL');
	const [ascending, setAscending] = useState(true);
	const [sortKey, setSortKey] = useState('NAME');
	const [overview, setOverview] = useState<FinancialOverviewDTO | null>(null);
	const [loading, setLoading] = useState(true);
	const [syncing, setSyncing] = useState(false);
	const { showAlert, showError } = useAlert();
	const service = new FinancialService();

	const load = async (count: number) => {
		setLoading(true);
		const response = await service.getOverview(count);
		if (!response) {
			showError('Não foi possível carregar a visão geral. Recarregue a página para tentar novamente.');
		}
		setOverview(response?.data || null);
		setLoading(false);
	};

	useEffect(() => {
		load(months);
	}, [months]);

	const sync = async () => {
		setSyncing(true);
		try {
			const response = await service.sync(months);
			showAlert(`Faturas atualizadas: ${response.data.bills}`);
			await load(months);
		} catch (error: any) {
			showError(typeof error?.response?.data === 'string' ? error.response.data : undefined);
		}
		setSyncing(false);
	};

	const rows = useMemo(() => {
		const term = search.trim().toLowerCase();
		const digits = term.replace(/\D/g, '');
		return (overview?.rows || [])
			.filter(row => matchesStatusFilter(row, overview?.months || [], statusFilter))
			.filter(row => !term || row.companyName?.toLowerCase().includes(term)
				|| (!!digits && (row.document || '').replace(/\D/g, '').includes(digits)))
			.sort((a, b) => compareOverviewRows(a, b, sortKey, ascending));
	}, [overview, search, ascending, sortKey, statusFilter]);

	const sortBy = (key: string) => {
		setAscending(sortKey === key ? !ascending : key === 'NAME');
		setSortKey(key);
	};

	const total = (month: string, status?: string) => rows.reduce((value, row) => value
		+ sum((row.bills[month] || []).filter(bill => !status || (status === 'PAID' ? bill.status === 'PAID' : bill.status !== 'PAID'))), 0);

	const lastSync = overview?.lastSync ? new Date(overview.lastSync).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'nunca';

	return (
		<div className="financial-overview">
			<div className="financial-filters">
				<FormControl size="small" sx={{ minWidth: 190 }}>
					<InputLabel id="financial-period">Visualizar:</InputLabel>
					<Select labelId="financial-period" label="Visualizar:" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
						{PERIODS.map(period => <MenuItem key={period} value={period}>{`Últimos ${period} meses`}</MenuItem>)}
					</Select>
				</FormControl>
				<FormControl size="small" sx={{ minWidth: 250 }}>
					<InputLabel id="financial-status-filter">Filtrar empresas</InputLabel>
					<Select labelId="financial-status-filter" label="Filtrar empresas" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
						{STATUS_FILTERS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
					</Select>
				</FormControl>
				<TextField size="small" label="Buscar por nome ou CNPJ" value={search} onChange={(e) => setSearch(e.target.value)}
					sx={{ width: { xs: '100%', md: '320px' } }}/>
				<div className="financial-sync">
					<Typography variant="body2" color="text.secondary">Atualizado em {lastSync}</Typography>
					<Button variant="outlined" startIcon={syncing ? <CircularProgress size={16}/> : <Sync/>} disabled={syncing} onClick={sync}>
						Atualizar agora
					</Button>
				</div>
			</div>
			{loading
				? <div className="financial-loading"><CircularProgress/></div>
				: <TableContainer className="financial-table">
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell>
									<TableSortLabel active={sortKey === 'NAME'} direction={sortKey === 'NAME' && !ascending ? 'desc' : 'asc'}
										onClick={() => sortBy('NAME')}>
										Empresa
									</TableSortLabel>
								</TableCell>
								<TableCell>CNPJ</TableCell>
								{overview?.months.map(month => (
									<TableCell key={month} align="right" sortDirection={sortKey === month ? (ascending ? 'asc' : 'desc') : false}>
										<TableSortLabel active={sortKey === month} direction={sortKey === month && !ascending ? 'desc' : 'asc'}
											onClick={() => sortBy(month)}>
											{monthLabel(month)}
										</TableSortLabel>
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map(row => (
								<TableRow key={row.companyId} hover>
									<TableCell>{row.companyName}</TableCell>
									<TableCell className="financial-nowrap">{row.document}</TableCell>
									{overview?.months.map(month => {
										const bills = row.bills[month] || [];
										return bills.length
											? <Tooltip key={month} title={<>{bills.map(bill => <div key={bill.idConta}>{`${formatCurrency(bill.value)} - ${bill.statusText}`}</div>)}</>}>
												<TableCell align="right" className="financial-nowrap" sx={{ backgroundColor: statusColor(cellStatus(bills)) }}>
													{formatCurrency(sum(bills))}
												</TableCell>
											</Tooltip>
											: <TableCell key={month} align="center">—</TableCell>;
									})}
								</TableRow>
							))}
							{!rows.length &&
								<TableRow>
									<TableCell colSpan={2 + (overview?.months.length || 0)} align="center">Nenhuma fatura encontrada.</TableCell>
								</TableRow>
							}
						</TableBody>
						<TableFooter className="financial-footer">
							{TOTALS.map(([label, status, color]) => (
								<TableRow key={label}>
									<TableCell colSpan={2} sx={{ color: `${color} !important` }}>{label}</TableCell>
									{overview?.months.map(month => (
										<TableCell key={month} align="right" className="financial-nowrap" sx={{ color: `${color} !important` }}>
											{formatCurrency(total(month, status))}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableFooter>
					</Table>
				</TableContainer>
			}
		</div>
	);
}

export default FinancialOverview
